import React, { useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toolbar } from 'primereact/toolbar';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dialog } from 'primereact/dialog';
import { Card } from 'primereact/card';
import { Column } from 'primereact/column';
import { Menu } from 'primereact/menu';
import * as generic from '../../components/Generico';
import { useLazyParams } from '../../hooks/useLazyParams';
import { TablaDinamica } from '../../components/TablaDinamica';
import { AnexosComponent } from '../../components/AnexosComponent';
import { useToken } from '../../components/useToken';
import { FiltroMultiempresa } from '../../components/FiltroMultiempresa';
import { EmpleadoService } from '../../service/EmpleadoService';
import { Empleado } from '../../models/ficha/Empleado';
import { EmpleadoDatosPersonales } from './common/EmpleadoDatosPersonales';
import { EmpleadoConyugue } from './common/EmpleadoConyugue';
import { EmpleadoEconomica } from './common/EmpleadoEconomica';
import { EmpleadoDeclaracion } from './common/EmpleadoDeclaracion';
import { DialogInstrucciones } from '../../components/ui/DialogInstrucciones';

export const Empleados = () => {
    const [catalogos, setCatalogos] = useState(null);
    const omodel = new Empleado({ formularioMatriz: catalogos?.formularioMatriz });
    const toast = useRef(null);
    const empleadoService = new EmpleadoService();
    const { getPerss, getRol } = useToken();
    // Estados globales del formulario
    const [empleado, setEmpleado] = useState(null);
    const [permss, setPermss] = useState(null);
    // Estados de navegación y UI
    const [dialogEmpleado, setDialogEmpleado] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [siCliente, setSiCliente] = useState(false);
    const [urls, setUrls] = useState(null);
    const [dialogAnexos, setDialogAnexos] = useState(false);
    const [dialogInstruccion, setDialogInstruccion] = useState(false);
    const [soloLectura, setSoloLectura] = useState(false);
    const [empresa, setEmpresa] = useState(null);
    const tablaEstado = useLazyParams({ modelo: omodel.model });
    const menuRef = useRef(null);
    const topRef = useRef(null);
    const [mostrarRegresarFlotante, setMostrarRegresarFlotante] = useState(false);

    useEffect(() => {
        setUrls({
            urlListado: 'ficha/empleado',
            urlDownload: 'ficha/empleado/descarga',
            urlUpload: 'ficha/empleado/store',
            udelete: 'ficha/empleado/eliminar-anexo',
            uenviar: 'ficha/empleado/enviar',
            uaprobar: 'ficha/empleado/aprobar',
        });
        const permisosToken = getPerss();
        const esClienteNotificado = permisosToken !== undefined;
        setSiCliente(esClienteNotificado);
        if (esClienteNotificado) { // FLUJO EMPLEADO NOTIFICADO (autoservicio)
            setPermss(permisosToken);
            editFicha(true);
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {        
        if (getPerss() === undefined) {
            loadLazyData();
        }
        // eslint-disable-next-line
    }, [tablaEstado.lazyParams]);

    // Efecto para observar el inicio del formulario
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setMostrarRegresarFlotante(!entry.isIntersecting);
            },
            { threshold: 0 }
        );
        const currentRef = topRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }
        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [dialogEmpleado]);

    const loadLazyData = () => {
        tablaEstado.setLoading(true);
        const _lazyParams = { ...tablaEstado.lazyParams };
        delete _lazyParams.rtipo;
        delete _lazyParams.empresa;
        if (permss == null) {
            _lazyParams.rtipo = true;
        }
        if (empresa) {
            _lazyParams.empresa = empresa;
        }
        const rol = getRol();
        if (!rol) {
            toast.current.show({ severity: 'warn', summary: 'Rol no seleccionado', detail: 'Por favor selecciona un rol antes de continuar.', life: 3000 });
            tablaEstado.setLoading(false);
            return;
        } else {
            _lazyParams.rol = rol;
        }
        empleadoService.getLista({ lazyEvent: JSON.stringify(_lazyParams) }).then(res => {
            tablaEstado.setTotalRecords(res.data.total);
            tablaEstado.setData(res.data.registros);
            if (res.data.perss !== undefined) {
                setPermss(res.data.perss);
                setCatalogos(res.data.catalogos);
            }
        }).finally(() => {
            tablaEstado.setLoading(false);
        });
    }

    const iniciarNuevo = () => { // Los catálogos ya existen puesto que se cargan en el listado para usuarios internos
        setSoloLectura(false);
        setDialogEmpleado(true);
        setEmpleado(null);
        setActiveIndex(0);
        setDialogInstruccion(true);
    }

    const editFicha = (esNotificado = false, isReadOnly = false) => {
        setSoloLectura(isReadOnly);
        const idConsulta = esNotificado ? 0 : empleado?.id;
        const necesitaCatalogos = catalogos === null;
        empleadoService.consultarEmpleado(idConsulta, null, { necesitaCatalogos: necesitaCatalogos }).then(res => {
            if (necesitaCatalogos && res.data.catalogos) {
                setCatalogos(res.data.catalogos);
            }
            if (!res.data.empleado) {
                iniciarNuevo();
                return;
            }
            setDialogEmpleado(true);
            setActiveIndex(0);
            setDialogInstruccion(true);
            // Sincronizamos el estado principal con lo que necesitamos para navegar
            nuevoEmpleado({
                id: res.data.empleado.id,
                nemonico: res.data.empleado.nemonico,
                tiene_conyuge: res.data.empleado.tiene_conyuge
            });
        });
    }

    const confirmDelete = () => {
        confirmDialog({
            message: '¿Está seguro de eliminar el formulario?',
            header: 'Confirmación',
            icon: 'pi pi-exclamation-triangle',
            accept: () => deleteEmpleado()
        });
    }

    const deleteEmpleado = () => {
        empleadoService.eliminarEmpleado(empleado.id).then(res => {
            loadLazyData();
            generic.funcSuccess(res, toast);
        }).catch(error => {
            generic.funcDeleteError(error, toast);
        });
    }

    // FUNCIÓN VITAL PARA MANTENER SINCRONIZADAS LAS SECCIONES
    const nuevoEmpleado = (nuevo) => {
        if (!nuevo) {
            setEmpleado(null);
            return;
        }
        setEmpleado(prev => ({ ...prev, ...nuevo }));
    }

    // CONTROLES DE NAVEGACIÓN (Siguiente / Anterior), saltando la Sección 2 si no tiene cónyuge/conviviente
    const handleNext = (datos_actualizados = null) => {
        // Si vienen datos nuevos del guardado, los fusionamos con el estado actual
        const empleadoActual = datos_actualizados ? { ...empleado, ...datos_actualizados } : empleado;
        if (activeIndex === 0 && !empleadoActual?.tiene_conyuge) { setActiveIndex(2); return; }
        if (activeIndex < 3) setActiveIndex(activeIndex + 1);
    };

    const handlePrev = () => {
        if (activeIndex === 2 && !empleado?.tiene_conyuge) { setActiveIndex(0); return; }
        if (activeIndex > 0) setActiveIndex(activeIndex - 1);
    };

    const regresar = () => {
        setActiveIndex(0);
        setDialogEmpleado(false);
        setSoloLectura(false);
        if (!siCliente) loadLazyData();
    }

    const reiniciar = () => {
        if (!siCliente) loadLazyData();
        setDialogEmpleado(false);
        setDialogAnexos(false);
    }

    const itemsMenu = () => {
        const items = [];
        if (permss?.consultar) {
            items.push({ label: 'Ver', icon: 'pi pi-eye', command: () => { editFicha(false, true); } });
        }
        if (permss?.actualizar && (empleado?.nemonico === 'BORRADOR' || empleado?.nemonico === 'ENVIADO')) {
            items.push({ label: 'Editar', icon: 'pi pi-pencil', command: () => { editFicha(); } });
        }
        if (permss?.consultar) {
            items.push({ label: 'Anexos formulario', icon: 'pi pi-folder-open', command: () => { setDialogAnexos(true); } });
        }
        if (permss?.eliminar && empleado?.nemonico === 'BORRADOR') {
            items.push({ label: 'Eliminar', icon: 'pi pi-trash', command: () => { confirmDelete(); } });
        }
        return [{ label: 'Opciones', items: items }];
    }

    const accionesTemplate = (rowData) => {
        return (
            <Button icon="pi pi-caret-down" type="button" className="p-button-rounded p-button-success mr-2" title="Opciones/Acciones" aria-controls="popup_menu" aria-haspopup
                onClick={(event) => { setEmpleado(rowData); menuRef.current.toggle(event); }} />
        );
    };

    const ColumnaAcciones = (
        <Column header="Acciones" body={accionesTemplate} style={{ width: '6rem', textAlign: 'center' }} />
    );

    const panelHeaderTemplate = () => (
        <div className="p-panel-header flex justify-content-between align-items-center flex-wrap gap-2 w-full">
            <div>
                <h3 className="m-0 text-xl font-bold p-text-secondary flex align-items-center">
                    <i className="pi pi-id-card mr-2"></i>
                    <span className="mr-2">{soloLectura ? 'VER' : (empleado?.id ? 'EDITAR' : 'NUEVO')} FORMULARIO DE EMPLEADO</span>
                    <Button type="button" icon="pi pi-question-circle" className="p-button-rounded p-button-text p-button-sm p-0 m-0 text-white hover:text-blue-200 transition-colors transition-duration-200"
                        style={{ width: '2rem', height: '2rem' }} onClick={() => setDialogInstruccion(true)} tooltip="Ver instrucciones" tooltipOptions={{ position: 'right' }} />
                </h3>
            </div>
            {!siCliente && (
                <Button tooltip="Regresar al listado" tooltipOptions={{ position: 'left' }} icon="pi pi-arrow-left" onClick={regresar} />
            )}
        </div>
    );

    return (
        <>
            <ConfirmDialog />
            <Toast ref={toast} />
            <Menu model={itemsMenu()} popup ref={menuRef} id="popup_menu" />
            {!dialogEmpleado && !siCliente && (
                <div className="fadein animation-duration-300">
                    {getRol() && getRol()?.ideg && <FiltroMultiempresa loadLazyData={loadLazyData} setEmpresa={setEmpresa} />}
                    <Toolbar className="mb-4 shadow-1 border-round" start={(getRol() && getRol()?.ideg) ? null : generic.leftToolbarTemplate(iniciarNuevo, 'Nuevo', permss)}
                        end={generic.rightToolbarTemplate(tablaEstado.data, omodel, 'Listado_empleados', 'LISTADO INFORMACIÓN EMPLEADO')} />
                    <Card style={{ maxWidth: '100%', margin: 'auto' }}>
                        <TablaDinamica modelo={omodel.model} estadoTabla={tablaEstado} datos={tablaEstado.data} totalRegistros={tablaEstado.totalRecords} dataKey="id" acciones={ColumnaAcciones} />
                    </Card>
                </div>
            )}
            {dialogEmpleado &&
                <div className="fadein animation-duration-300">
                    <Panel headerTemplate={panelHeaderTemplate} className="panel-ficha shadow-2">
                        <div ref={topRef} style={{ position: 'absolute', top: '0', left: '0', width: '1px', height: '80px', pointerEvents: 'none', visibility: 'hidden' }} />
                        <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)} className="form-tabview">
                            <TabPanel header="Sección 1" leftIcon="pi pi-id-card mr-2">
                                <EmpleadoDatosPersonales toast={toast} id={empleado?.id} permss={permss} catalogos={catalogos}
                                    next={handleNext} nuevoEmpleado={nuevoEmpleado} soloLectura={soloLectura} />
                            </TabPanel>

                            <TabPanel header="Sección 2" leftIcon="pi pi-users mr-2" disabled={!empleado?.id || !empleado?.tiene_conyuge}>
                                <EmpleadoConyugue toast={toast} id={empleado?.id} permss={permss} catalogos={catalogos}
                                    handlePrev={handlePrev} next={handleNext} soloLectura={soloLectura} />
                            </TabPanel>

                            <TabPanel header="Sección 3" leftIcon="pi pi-wallet mr-2" disabled={!empleado?.id}>
                                <EmpleadoEconomica toast={toast} id={empleado?.id} permss={permss} catalogos={catalogos} tieneConyuge={empleado?.tiene_conyuge}
                                    handlePrev={handlePrev} next={handleNext} soloLectura={soloLectura} />
                            </TabPanel>

                            <TabPanel header="Sección 4" leftIcon="pi pi-shield mr-2" disabled={!empleado?.id}>
                                <EmpleadoDeclaracion toast={toast} id={empleado?.id} permss={permss} catalogos={catalogos}
                                    handlePrev={handlePrev} mostrarAnexos={() => setDialogAnexos(true)} soloLectura={soloLectura} />
                            </TabPanel>
                        </TabView>
                    </Panel>

                    {mostrarRegresarFlotante && !siCliente && (
                        <div className="fadein scalein" style={{ position: 'fixed', top: '65px', right: '30px', zIndex: 999 }}>
                            <Button icon="pi pi-arrow-left" onClick={regresar} rounded className="p-button-primary shadow-4" style={{ width: '45px', height: '45px' }} tooltip="Regresar al listado" tooltipOptions={{ position: 'left' }} />
                        </div>
                    )}
                </div>
            }

            <DialogInstrucciones visible={dialogInstruccion} onHide={() => setDialogInstruccion(false)} title={omodel.titulo} items={omodel.pasosInstrucciones} />
            <Dialog visible={dialogAnexos} header="Gestión de Anexos" modal className="p-fluid modal-ssize" onHide={() => setDialogAnexos(false)} style={{ width: '800px' }}>
                <AnexosComponent tipo="empleado" urls={urls} idp={empleado?.id} toast={toast} permss={permss} setDialogSubir={setDialogAnexos} reiniciar={reiniciar} estado={empleado?.nemonico} soloLectura={soloLectura} />
            </Dialog>
        </>
    )
}
